import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { nivelConsultorService } from '@/services/nivelConsultorService'
import {
  COR_NIVEL_REGEX,
  NIVEL_DESCRICAO_MAX,
  NIVEL_NOME_MAX,
  NivelConsultor,
  NivelConsultorRequest,
  mensagemErroNivel,
} from '@/types/nivelConsultor'
import { ChipNivel } from './ChipNivel'

export interface DialogoNivelProps {
  aberto: boolean
  /** `null` = novo nível. */
  nivel: NivelConsultor | null
  /** `GET /configuracao`: com `false` a seção de critérios nem aparece (§7). */
  criteriosHabilitado: boolean
  onFechar: () => void
  onSalvo: () => void
}

/**
 * Cadastro/edição de nível (ADMIN — AN-10 §3/§10): nome, descrição, cor (seletor + hex) e
 * ordem opcional, com prévia do chip. Nenhum nome ou cor sugerido — tudo é digitado pelo
 * administrador. Erros do backend (409/400/422) aparecem dentro do diálogo.
 */
export const DialogoNivel: FC<DialogoNivelProps> = ({ aberto, nivel, criteriosHabilitado, onFechar, onSalvo }) => {
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [cor, setCor] = useState('')
  const [ordem, setOrdem] = useState('')
  const [criterios, setCriterios] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!aberto) return
    setNome(nivel?.nome ?? '')
    setDescricao(nivel?.descricao ?? '')
    setCor(nivel?.cor ?? '')
    setOrdem(nivel ? String(nivel.ordem) : '')
    setCriterios(nivel?.criteriosTexto ?? '')
    setErro(null)
  }, [aberto, nivel])

  const corValida = COR_NIVEL_REGEX.test(cor.trim())
  const ordemNum = ordem.trim() === '' ? null : Number(ordem)
  const ordemValida = ordemNum === null || (Number.isInteger(ordemNum) && ordemNum >= 1)
  const podeSalvar = nome.trim().length > 0 && descricao.trim().length > 0 && corValida && ordemValida && !salvando

  const salvar = async () => {
    if (!podeSalvar) return
    setSalvando(true)
    setErro(null)
    const req: NivelConsultorRequest = {
      nome: nome.trim(),
      descricao: descricao.trim(),
      cor: cor.trim().toUpperCase(),
      ordem: ordemNum,
    }
    if (criteriosHabilitado) req.criteriosTexto = criterios.trim() || null
    try {
      if (nivel) await nivelConsultorService.atualizar(nivel.id, { ...req, versao: nivel.versao })
      else await nivelConsultorService.criar(req)
      onSalvo()
    } catch (e) {
      setErro(mensagemErroNivel(e, 'Não foi possível salvar o nível'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>{nivel ? 'Editar nível' : 'Novo nível'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erro && (
            <Alert severity="error" role="alert">
              {erro}
            </Alert>
          )}
          <TextField
            label="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            inputProps={{ maxLength: NIVEL_NOME_MAX }}
            helperText={`${nome.length}/${NIVEL_NOME_MAX}`}
            required
            fullWidth
          />
          <TextField
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            inputProps={{ maxLength: NIVEL_DESCRICAO_MAX }}
            helperText={`O consultor vê esta descrição. ${descricao.length}/${NIVEL_DESCRICAO_MAX}`}
            required
            fullWidth
            multiline
            minRows={3}
          />
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              component="input"
              type="color"
              aria-label="Seletor de cor"
              value={corValida ? cor.trim() : '#000000'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCor(e.target.value.toUpperCase())}
              sx={{ width: 56, height: 40, p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            />
            <TextField
              label="Cor (hex)"
              value={cor}
              onChange={(e) => setCor(e.target.value)}
              placeholder="#RRGGBB"
              required
              error={cor.length > 0 && !corValida}
              helperText={cor.length > 0 && !corValida ? 'Use o formato #RRGGBB' : 'Obrigatória'}
              inputProps={{ maxLength: 7 }}
              sx={{ width: 180 }}
            />
            <TextField
              label="Ordem"
              value={ordem}
              onChange={(e) => setOrdem(e.target.value.replace(/\D/g, ''))}
              error={!ordemValida}
              helperText={nivel ? ' ' : 'Opcional: vai para o fim'}
              inputProps={{ inputMode: 'numeric' }}
              sx={{ width: 140 }}
            />
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Prévia
            </Typography>
            {corValida && nome.trim() ? (
              <ChipNivel nome={nome.trim()} cor={cor.trim()} size="medium" />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Informe nome e cor para ver a prévia
              </Typography>
            )}
          </Box>
          {criteriosHabilitado && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Critérios (desativado)
              </Typography>
              <TextField
                label="Critérios — texto interno"
                value={criterios}
                onChange={(e) => setCriterios(e.target.value)}
                helperText="Documentação interna; não é interpretada e não tem nenhum efeito."
                fullWidth
                multiline
                minRows={2}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DialogoNivel
